import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Closing";
import { api, errorMessage } from "@/lib/api";
import { usd } from "@/lib/addons";

const POLL_MS = 2000, MAX_POLLS = 6;

export default function BookSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", booking: null, error: "" });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", error: "Missing payment session." }); return; }
    let polls = 0, cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") return setState({ status: "paid", booking: data.booking });
        if (data.payment_status === "expired") return setState({ status: "expired", booking: data.booking });
        if (++polls >= MAX_POLLS) return setState({ status: "pending", booking: data.booking });
        setTimeout(poll, POLL_MS);
      } catch (e) { if (!cancelled) setState({ status: "error", error: errorMessage(e) }); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const b = state.booking;
  return (
    <div className="min-h-screen bg-white" data-testid="booking-success-page">
      <Nav />
      <main className="container-x py-12 sm:py-16 max-w-3xl">
        {state.status === "checking" && <p className="flex items-center gap-2 text-sec" data-testid="payment-checking"><Loader2 size={16} className="animate-spin" /> Confirming your payment…</p>}
        {state.status === "paid" && b && (
          <div className="reveal" data-testid="payment-success">
            <p className="eyebrow mb-4">Reserved</p>
            <h1 className="h-editorial">Your assessment is reserved</h1>
            <p className="lead mt-5">We’ve received your {usd(b.deposit_cents / 100)} deposit{b.addons?.length ? " and payment for your selected laboratory add-ons" : ""}. We’ll confirm your appointment details shortly.</p>
            <dl className="mt-7 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm rounded-md border border-divider p-5">
              <dt className="mono text-sec pt-0.5">Reference</dt><dd className="font-mono text-base text-ink" data-testid="booking-reference">{b.reference}</dd>
              <dt className="mono text-sec pt-0.5">Address</dt><dd className="text-ink">{b.address}</dd>
              <dt className="mono text-sec pt-0.5">Visit</dt><dd className="text-ink">{b.date_label} · {b.window_label}</dd>
              <dt className="mono text-sec pt-0.5">Add-ons</dt><dd className="text-ink">{b.addons.length ? b.addons.map((a) => a.name).join(", ") : "None selected"}</dd>
              <dt className="mono text-sec pt-0.5">Paid today</dt><dd className="font-mono text-ink" data-testid="paid-today">{usd(b.amount_paid_cents / 100)}</dd>
              <dt className="mono text-sec pt-0.5">Remaining balance</dt><dd className="text-ink"><span className="font-mono">{usd(b.remaining_balance_cents / 100)}</span> <span className="text-sec">due 24 hours before your visit</span></dd>
            </dl>
          </div>
        )}
        {(state.status === "pending" || state.status === "expired") && (
          <div data-testid="payment-pending">
            <h1 className="h-editorial">{state.status === "expired" ? "Checkout session expired." : "Payment still processing."}</h1>
            <p className="lead mt-5">{state.status === "expired" ? "No charge was made. Start your reservation again when you’re ready." : "We haven’t received confirmation from the payment provider yet. If you completed payment, you’ll receive an email once it clears."}{b ? ` Reference ${b.reference}.` : ""}</p>
          </div>
        )}
        {state.status === "error" && (
          <div data-testid="payment-error">
            <h1 className="h-editorial">We couldn’t find that payment session.</h1>
            <p className="lead mt-5">If you completed payment, your confirmation email has the details. Otherwise, you can start a new reservation.</p>
            <p className="mt-3 text-xs text-sec" role="alert">{state.error}</p>
          </div>
        )}
        <Link to="/" className="link-arrow mt-10" data-testid="done-home-link"><ArrowLeft size={16} /> Back to the site</Link>
      </main>
      <Footer />
    </div>
  );
}
